import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Check, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import useListado from '../../hooks/useListado.js';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, today } from '../finanzas-cxp/shared.js';
import { exportTablePdf, exportTableExcel } from '../../utils/exportUtils.js';

/**
 * Traspasos entre Bancos — página BESPOKE (no `ResourcePage` genérico + `FormModal`).
 *
 * TAREA 3 de la segunda ronda de la auditoría CxC 2026-09-28 (ver DUDAS.md, gap #7):
 * V1 (`IbuFront/.../Bancos/ListadoBancos.jsx`, modal "Traspaso" líneas ~723-891) tiene
 * dos cosas que el `ResourcePage`/`FormModal` genérico (usado antes aquí vía
 * `RESOURCES_FINANZAS_CXC['bancos-traspasos']`) no soporta:
 *   1. Calculadora en vivo (`calculateDestinationAndCommission`, líneas ~82-101 de
 *      V1): dado el importe origen + tipo de cambio + % de comisión del banco
 *      destino, calcula en vivo el importe bruto en la moneda destino (multiplica si
 *      origen=MXN/destino=USD por `1/tipoCambio`, divide si es al revés,
 *      exactamente el mismo si ambas monedas coinciden), la comisión $ resultante y
 *      el importe NETO a agregar en destino. Los 3 campos (Importe, Tipo de Cambio,
 *      % Comisión Destino) están ligados: cambiar cualquiera recalcula los otros 2
 *      derivados (Comisión $, Importe a agregar en Destino).
 *   2. Filtro del `<select>` de Banco Destino (V1 líneas ~746-761): excluye el banco
 *      ya elegido como Origen (`a._id != bancoOrigen`) Y, por nombre literal, los
 *      bancos "Devolucion"/"Bonificacion" (`a.banco != "Devolucion" && a.banco !=
 *      "Bonificacion"`) -- se replica igual, aunque la auditoría de Tarea 2 (ver
 *      `bancos.routes.js`) confirmó que HOY no existe ningún banco con esos nombres
 *      exactos en el catálogo real de `ibuPruebas`: se porta tal cual (fidelidad 1:1
 *      con V1, código muerto inofensivo si esos bancos no existen, útil si algún día
 *      se vuelven a dar de alta con esos nombres).
 * El listado/paginación/orden/export siguen siendo server-side (mismo `useListado`
 * que usa `ResourcePage`, `bancos-traspasos.service.js#listar` SÍ pagina) -- solo el
 * formulario de alta se volvió bespoke, mismo criterio que `CargosPage.jsx`/
 * `CargosContenedorPage.jsx` para sus propias calculadoras ligadas.
 */
const RECURSO = 'bancos-traspasos';
const ROUTE = 'bancos-traspasos';

const MONEDA_OPTS = ['MXN', 'USD'];

/** Réplica exacta de V1 `calculateDestinationAndCommission` (ListadoBancos.jsx ~L82-101). */
function calcularDestinoYComision(importeOrigen, tipoCambio, comisionPorcentaje, monedaOrigen, monedaDestino) {
  let bruto = 0;
  const tc = Number(tipoCambio) || 0;
  const imp = Number(importeOrigen) || 0;
  if (monedaOrigen === 'MXN' && monedaDestino === 'USD') bruto = tc ? imp / tc : 0;
  else if (monedaOrigen === 'USD' && monedaDestino === 'MXN') bruto = imp * tc;
  else if (monedaOrigen === monedaDestino) bruto = imp;
  const comision = (bruto * (Number(comisionPorcentaje) || 0)) / 100;
  const neto = bruto - comision;
  return { bruto, comision, neto };
}

function valoresIniciales(bancoOrigenDefault) {
  return {
    fecha: today(), bancoOrigen: bancoOrigenDefault || '', bancoDestino: '',
    monedaOrigen: 'MXN', monedaDestino: 'MXN', importe: 0, tipoCambio: 1,
    comisionBancoDestinoPorcentaje: 0, comisionBancoDestinoCantidad: 0, importeDestino: 0,
    observaciones: 'NA',
  };
}

/** POST de un traspaso (compartido por la página de Traspasos y el botón "Traspaso" por fila de Bancos). */
export async function postTraspaso(v) {
  const payload = {
    fecha: v.fecha, bancoOrigen: v.bancoOrigen, bancoDestino: v.bancoDestino,
    importe: Number(v.importe), importeDestino: Number(v.importeDestino) || Number(v.importe),
    monedaOrigen: v.monedaOrigen, monedaDestino: v.monedaDestino, tipoCambio: Number(v.tipoCambio) || 1,
    observaciones: v.observaciones || 'NA',
    comisionBancoDestinoCantidad: Number(v.comisionBancoDestinoCantidad) || 0,
    comisionBancoDestinoPorcentaje: Number(v.comisionBancoDestinoPorcentaje) || 0,
  };
  await apiClient.post(`/${ROUTE}`, payload);
}

/**
 * Modal de traspaso. Si recibe `bancoOrigenFijo` (fila de Bancos, igual que el modal
 * "Traspaso de Banco X" de V1) el origen viene fijo y no se muestra el select de origen.
 */
export function TraspasoForm({ open, onClose, bancos, onSubmit, bancoOrigenFijo }) {
  const [v, setV] = useState(valoresIniciales());
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (bancoOrigenFijo) setV({ ...valoresIniciales(bancoOrigenFijo._id), monedaOrigen: bancoOrigenFijo.moneda || 'MXN' });
    else setV(valoresIniciales());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  function elegirOrigen(id) {
    const banco = bancos.find((b) => b._id === id);
    setV((s) => ({
      ...valoresIniciales(id),
      monedaOrigen: banco?.moneda || 'MXN',
    }));
  }

  // V1 `jalaBancoDestino`: al elegir destino se reinician importe/TC/comisión/importe destino.
  function elegirDestino(id) {
    const banco = bancos.find((b) => b._id === id);
    setV((s) => ({
      ...s, bancoDestino: id, monedaDestino: banco?.moneda || s.monedaDestino,
      importe: 0, importeDestino: 0, comisionBancoDestinoCantidad: 0, comisionBancoDestinoPorcentaje: 0, tipoCambio: 1,
    }));
  }

  // ---- Calculadora ligada: Importe / Tipo de Cambio / % Comisión Destino ----
  // (réplica de V1 `onValueChange` de Importe/TipoCambio/%Comisión, ver cabecera).
  function recalcular(cambios) {
    setV((s) => {
      const next = { ...s, ...cambios };
      const { comision, neto } = calcularDestinoYComision(next.importe, next.tipoCambio, next.comisionBancoDestinoPorcentaje, next.monedaOrigen, next.monedaDestino);
      return { ...next, comisionBancoDestinoCantidad: comision, importeDestino: neto };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!v.bancoOrigen || !v.bancoDestino || !v.fecha || !(Number(v.importe) > 0)) {
      Swal.fire({ icon: 'error', title: 'Faltan datos', text: 'Banco origen, banco destino, fecha e importe son obligatorios' });
      return;
    }
    setGuardando(true);
    try {
      await onSubmit(v);
      Swal.fire({ icon: 'success', title: 'Good job!', text: 'Creado con exito', timer: 1500, showConfirmButton: false });
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: err.message });
    } finally {
      setGuardando(false);
    }
  }

  const bancosOrdenados = [...bancos].sort((a, b) => (a.banco > b.banco ? 1 : -1));
  // Filtro de Banco Destino (réplica exacta de V1, ver cabecera del archivo): excluye
  // el banco origen elegido y, por nombre literal, "Devolucion"/"Bonificacion".
  const bancosDestino = bancosOrdenados.filter((b) => b._id !== v.bancoOrigen && b.banco !== 'Devolucion' && b.banco !== 'Bonificacion');
  const monedasDistintas = v.monedaOrigen !== v.monedaDestino;

  return (
    <Modal open={open} onClose={onClose} title={bancoOrigenFijo ? `Traspaso de Banco ${bancoOrigenFijo.banco}` : 'Nuevo Traspaso entre Bancos'} width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cancelar</button>
          <button type="submit" form="traspaso-form" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      )}
    >
      <form id="traspaso-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)}
            className="input-base" />
        </div>
        {!bancoOrigenFijo && (
        <div>
          <label className="label">Banco origen</label>
          <select required value={v.bancoOrigen} onChange={(e) => elegirOrigen(e.target.value)}
            className="select-base">
            <option value="">Selecciona...</option>
            {bancosOrdenados.map((b) => <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>)}
          </select>
        </div>
        )}
        <div>
          <label className="label">Banco Destino</label>
          <select required value={v.bancoDestino} onChange={(e) => elegirDestino(e.target.value)} disabled={!v.bancoOrigen}
            className="select-base">
            <option value="">Selecciona</option>
            {bancosDestino.map((b) => <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Importe</label>
          <input type="number" min="0" step="any" required value={v.importe} onChange={(e) => recalcular({ importe: e.target.value })}
            className="input-base" />
        </div>
        {monedasDistintas && (
          <>
            <div>
              <label className="label">Tipo de Cambio</label>
              <input type="number" min="0" step="any" required value={v.tipoCambio} onChange={(e) => recalcular({ tipoCambio: e.target.value })}
                className="input-base" />
            </div>
            <div>
              <label className="label">% Comision Banco Destino</label>
              <input type="number" min="0" max="100" step="any" value={v.comisionBancoDestinoPorcentaje} onChange={(e) => recalcular({ comisionBancoDestinoPorcentaje: e.target.value })}
                className="input-base" />
            </div>
            <div>
              <label className="label">Importe Comision Destino</label>
              <input type="number" disabled value={Number(v.comisionBancoDestinoCantidad).toFixed(2)} className="input-base" />
            </div>
            <div>
              <label className="label">Importe a agregar en Destino</label>
              <input type="number" disabled value={Number(v.importeDestino).toFixed(2)} className="input-base" />
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <label className="label">Observaciones</label>
          <input type="text" value={v.observaciones} onChange={(e) => set('observaciones', e.target.value)}
            className="input-base" />
        </div>
      </form>
    </Modal>
  );
}

export default function BancosTraspasosPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${RECURSO}:ver`);
  const puedeCrear = hasPermiso(`${RECURSO}:crear`);
  const puedeEliminar = hasPermiso(`${RECURSO}:eliminar`);

  const [bancos, setBancos] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmando, setConfirmando] = useState(null);
  const [exportando, setExportando] = useState(null);

  useEffect(() => {
    apiClient.get('/bancos', { params: { limit: 500 } }).then(({ data }) => setBancos(data.data || [])).catch(() => setBancos([]));
  }, []);

  const listar = useCallback(
    (params, opts) => apiClient.get(`/${ROUTE}`, { params, signal: opts?.signal }).then((r) => r.data),
    []
  );
  const { filas, paginacion, cargando, error, pagina, setPagina, sortBy, sortOrder, ordenarPor, busqueda, setBusqueda, recargar } =
    useListado({ listar, ordenPor: 'fecha' });

  async function guardar(v) {
    const payload = {
      fecha: v.fecha, bancoOrigen: v.bancoOrigen, bancoDestino: v.bancoDestino,
      importe: Number(v.importe), importeDestino: Number(v.importeDestino) || Number(v.importe),
      monedaOrigen: v.monedaOrigen, monedaDestino: v.monedaDestino, tipoCambio: Number(v.tipoCambio) || 1,
      observaciones: v.observaciones || 'NA',
      comisionBancoDestinoCantidad: Number(v.comisionBancoDestinoCantidad) || 0,
      comisionBancoDestinoPorcentaje: Number(v.comisionBancoDestinoPorcentaje) || 0,
    };
    await apiClient.post(`/${ROUTE}`, payload);
    await recargar();
  }

  async function cancelarTraspaso(row) {
    if (confirmando !== row._id) { setConfirmando(row._id); return; }
    setConfirmando(null);
    try {
      await apiClient.delete(`/${ROUTE}/${row._id}`);
      await recargar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo cancelar', text: e.message });
    }
  }

  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'origen', label: 'Origen' },
    { key: 'destino', label: 'Destino' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
    { key: 'tipoCambio', label: 'T.C.' },
  ];

  async function traerTodoFiltrado() {
    const { data } = await apiClient.get(`/${ROUTE}`, { params: { search: busqueda || undefined, sortBy, sortOrder, page: 1, limit: 100000 } });
    return (data?.data ?? []).map((r) => ({
      fecha: r.fecha,
      origen: r.bancoOrigen?.banco || r.bancoOrigen?.[0]?.banco || '—',
      destino: r.bancoDestino?.banco || r.bancoDestino?.[0]?.banco || '—',
      importe: r.importe, tipoCambio: r.tipoCambio,
    }));
  }

  async function exportarPDF() {
    setExportando('pdf');
    try { exportTablePdf('Traspasos entre Bancos', columnasExport, await traerTodoFiltrado()); }
    catch (e) { Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message }); }
    finally { setExportando(null); }
  }
  async function exportarExcel() {
    setExportando('excel');
    try { exportTableExcel('Traspasos entre Bancos', columnasExport, await traerTodoFiltrado(), { sheetName: 'Traspasos' }); }
    catch (e) { Swal.fire({ icon: 'error', title: 'No se pudo exportar', text: e.message }); }
    finally { setExportando(null); }
  }

  const columnas = [
    { key: 'fecha', label: 'Fecha', sortable: true },
    { key: 'bancoOrigen', label: 'Origen', render: (r) => r.bancoOrigen?.banco || r.bancoOrigen?.[0]?.banco || '—' },
    { key: 'bancoDestino', label: 'Destino', render: (r) => r.bancoDestino?.banco || r.bancoDestino?.[0]?.banco || '—' },
    { key: 'importe', label: 'Importe', className: 'text-right', sortable: true, render: (r) => formatMoney(r.importe) },
    { key: 'tipoCambio', label: 'T.C.', sortable: true },
    { key: 'is_active', label: 'Estatus', render: (r) => (r.is_active === 'No' ? <span className="badge-danger">Cancelado</span> : <span className="badge-success">Activo</span>) },
    puedeEliminar && {
      key: '__acciones', label: 'Acciones', sortable: false, className: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
          {row.is_active !== 'No' && (
            <button
              title={confirmando === row._id ? 'Confirmar' : 'Cancelar'}
              aria-label={confirmando === row._id ? 'Confirmar' : 'Cancelar'}
              className={`p-1.5 rounded-md hover:bg-slate-100 ${confirmando === row._id ? 'text-success-700' : 'text-danger-500'}`}
              onClick={() => cancelarTraspaso(row)}
            >
              {confirmando === row._id ? <Check size={15} /> : <Trash2 size={15} />}
            </button>
          )}
        </div>
      ),
    },
  ].filter(Boolean);

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Traspaso entre Bancos</h1>
        {puedeCrear && (
          <button onClick={() => setFormOpen(true)} className="btn-primary">
            + Nuevo Traspaso
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar..."
          className="input-base w-64" />
        <button type="button" onClick={exportarPDF} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
          {exportando === 'pdf' ? 'Generando...' : 'PDF'}
        </button>
        <button type="button" onClick={exportarExcel} disabled={!!exportando} className="btn-outline text-xs px-3 py-1.5">
          {exportando === 'excel' ? 'Generando...' : 'Excel'}
        </button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable columns={columnas} data={filas} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor} />
      <Pagination pagination={paginacion} page={pagina} onChange={setPagina} />

      <TraspasoForm open={formOpen} onClose={() => setFormOpen(false)} bancos={bancos} onSubmit={guardar} />
    </div>
  );
}
